import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import Autocomplete from '@mui/material/Autocomplete';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { DialogTitleWithClose } from '@/components/common/DialogTitleWithClose';
import { ConfigContext } from '@/Contexts/ConfigContext';
import { DashboardContext } from '@/Contexts/DashboardContext';
import { SelectedPartnerContext } from '@/Contexts/SelectedPartnerContext';
import { TokenContext } from '@/Contexts/TokenContext';
import { UsersContext } from '@/Contexts/UsersContext';
import useApplication from '@/hooks/useApplication';
import { useCustomFetch } from '@/hooks/useCustomFetch';
import usePartner from '@/hooks/usePartner';
import { ApplicationTypes } from '@/models/ApplicationType';
import { CountryProps } from '@/models/Country';
import { PartnerProps } from '@/models/Partner';
import { PartnerDashboardDetailsProps, PartnerDashboardProps } from '@/models/PartnerDashboard';
import { ProgramNames } from '@/models/Program';
import { UserType, userTypeAsString } from '@/models/User';
import {
  ApplicationStatus,
  ApplicationStatusesText,
  baseUrl,
  USCountryId,
} from '@/utils/constants';
import { YearsRecognizedByProgram } from '@/utils/YearsRecognizedUtils';

export default function PartnerMergeModal({
  partnerMergeModalOpen,
  setPartnerMergeModalOpen,
  theme,
  selectedProgramId,
  selectedPartners,
  partnerInfo,
  handleRowUpdate,
}: {
  partnerMergeModalOpen: boolean;
  setPartnerMergeModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  theme: Theme;
  selectedProgramId: ProgramNames;
  selectedPartners: PartnerProps[];
  partnerInfo: PartnerDashboardDetailsProps;
  handleRowUpdate: (updatedRow: PartnerDashboardProps) => void;
}) {
  const { customFetch } = useCustomFetch();
  const partnerUrl = useMemo(() => `${baseUrl}applications`, []);
  const { authResult } = useContext(TokenContext);
  const { userType } = useContext(UsersContext);
  const { allStates, allCountries } = useContext(ConfigContext);
  const { partnersAll, updatePartnersAll } = useContext(SelectedPartnerContext);
  const { getOneApplication } = useApplication();

  const {
    currentApps,
    reviewApps,
    historicApps,
    updateCurrentApps,
    updateReviewApps,
    updateHistoricApps,
  } = useContext(DashboardContext);

  const { getPartnerDataGridDetails, getPartner, postPartnersMerge } = usePartner();

  const [postingPartner, setPostingPartner] = useState<boolean>(false);

  const [partnerOptions, setPartnerOptions] = useState<any[]>([]);

  const [modalPartnerId, setModalPartnerId] = useState<number | null>(null);
  const [modalPartner, setModalPartner] = useState<any>({});
  const [selectedModalPartnerOption, setSelectedModalPartnerOption] = useState({
    id: '',
    label: '',
  });

  const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(null);
  const [selectedPartner, setSelectedPartner] = useState<any>({});
  const [selectedPartnerOption, setSelectedPartnerOption] = useState({
    id: '',
    label: '',
  });

  const [partnerAppsLoading, setPartnerAppsLoading] = useState<boolean>(false);
  const [selectedPartnerAppsLoading, setSelectedPartnerAppsLoading] = useState<boolean>(false);

  const [partnerContactLoading, setPartnerContactLoading] = useState<boolean>(false);
  const [selectedPartnerContactLoading, setSelectedPartnerContactLoading] =
    useState<boolean>(false);

  const [modalPartnerApps, setModalPartnerApps] = useState<any>([]);
  const [selectedPartnerApps, setSelectedPartnerApps] = useState<any>([]);

  const [modalPartnerHistoricApps, setModalPartnerHistoricApps] = useState<any>([]);
  const [selectedPartnerHistoricApps, setSelectedPartnerHistoricApps] = useState<any>([]);

  const [modalPartnerContact, setModalPartnerContact] = useState<any>([]);
  const [selectedPartnerContact, setSelectedPartnerContact] = useState<any>([]);

  const [modalPartnerAppPrimaryContact, setModalPartnerAppPrimaryContact] = useState<any>([]);
  const [selectedPartnerAppPrimaryContact, setSelectedPartnerAppPrimaryContact] = useState<any>([]);

  const [showWarning, setShowWarning] = useState<boolean>(false);

  const getPartnerApps = useCallback(
    async (partnerId: number) => {
      let allApps = [];

      const url = `${partnerUrl}/simple?partnerId=${partnerId}&page=1&itemsPerPage=100000`;
      allApps = await customFetch<unknown[]>(url, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      });

      allApps.forEach((app: any) => {
        app.statusDescription = ApplicationStatusesText[app.status as ApplicationStatus] ?? '';
      });

      return allApps;
    },
    [authResult, customFetch, partnerUrl]
  );

  const getPartnerContact = useCallback(
    async (contactId: any) => {
      if (contactId) {
        const fetchUrl = `${baseUrl}contacts/${contactId}`;

        const response = await customFetch(fetchUrl, {
          method: 'GET',
          headers: {
            accept: 'application/json',
            'content-type': 'application/json',
            Authorization: `Bearer ${authResult.accessToken}`,
          },
        });

        return response;
      } else {
        return null;
      }
    },
    [authResult, customFetch]
  );

  const updatePartners = (p: any, sid: any) => {
    const pa = { ...partnersAll };
    let ps = [...pa[selectedProgramId]];
    ps = ps.filter((x) => x.id != sid);
    const i = ps.map((x) => x.id).indexOf(p.id);
    if (ps[i]) {
      const partner = {
        ...ps[i],
        ...p,
      };
      ps[i] = partner;
      pa[selectedProgramId] = ps;
      updatePartnersAll(pa);
    }
  };

  const updateApplicationsAll = (a: any) => {
    const ca = [...currentApps];
    const i = ca.map((x) => x.applicationId).indexOf(a.applicationId);
    if (ca[i]) {
      ca[i] = a;
      updateCurrentApps(ca);
    }

    const ra = [...reviewApps];
    const j = ra.map((x) => x.applicationId).indexOf(a.applicationId);

    const isReview =
      (userType == UserType.Adf && a.status === ApplicationStatus.PendingAdfReview) ||
      (userType == UserType.StateCoordinator && a.status === ApplicationStatus.PendingStateReview);

    if (ra[j] && isReview) {
      ra[j] = a;
      updateReviewApps(ra);
    } else if (ra[j] && !isReview) {
      const raf = ra.filter((r) => r.applicationId != a.applicationId);
      updateReviewApps(raf);
    } else if (!ra[j] && isReview) {
      ra.push(a);
      updateReviewApps(ra);
    }

    const ha = [...historicApps];
    const k = ha.map((x) => x.applicationId).indexOf(a.applicationId);

    if (ha[k]) {
      ha[k] = a;
      updateHistoricApps(ha);
    }
  };

  const updatePartnerMerge = () => {
    if (!modalPartnerId || !selectedPartnerId) return;

    setPostingPartner(true);
    const postData = {
      partnerIdIn: modalPartnerId,
      partnerIdOut: selectedPartnerId,
    };

    postPartnersMerge(postData)
      .then(() => {
        getPartner(modalPartnerId).then((partner: PartnerProps) => {
          handleRowUpdate({
            ...partnerInfo,
            ...partner,
          });

          updatePartners(partner, selectedPartnerId);

          getPartnerApps(modalPartnerId).then((apps: any) => {
            apps.forEach((a: any) => {
              updateApplicationsAll(a);
            });

            setPartnerMergeModalOpen(false);
          });
        });
      })
      .finally(() => {
        setPostingPartner(false);
      });
  };

  const getStateName = useCallback(
    (stateId: any) => {
      if (!stateId || allStates.length == 0) return '';

      const state = allStates.find((s: any) => s.id == stateId);
      return state ? state.abbrev : '';
    },
    [allStates]
  );

  const getCountryName = useCallback(
    (countryId: any) => {
      if (!countryId || allCountries.length == 0) return '';

      const country = allCountries.find((c: CountryProps) => c.id == countryId);
      return country ? country.name : '';
    },
    [allCountries]
  );

  const getPartnerPrimaryContact = useCallback(
    (contact: any) => {
      const pc: any = [];
      pc.push(`${contact.firstName} ${contact.lastName}`);
      pc.push(`${contact.streetAddress1}`);
      pc.push(`${contact?.city}, ${getStateName(contact?.stateId)} ${contact?.zipCode}`);
      pc.push(`${getCountryName(contact.countryId)}`);
      return pc;
    },
    [getCountryName, getStateName]
  );

  const getPartnerCurrentApps = useCallback(
    (apps: any, partnerType: any) => {
      const ca = apps.filter(
        (a: any) => a.applicationCycleActive == 1 && a.programId == selectedProgramId
      );
      ca.sort((a: any, b: any) => {
        if (a.applicationTypeId < b.applicationTypeId) {
          return -1;
        }
      });
      const currentPartnerApps = ca.map((a: any) => {
        let app = `${a.applicationId} (${a.applicationType
          .replace('Tree City', 'TC')
          .replace('Tree Cities', 'TC')
          .replace('-Specific', '')}, ${a.statusDescription
          .replace('Pending ', '')
          .replace('Arbor Day Foundation', 'ADF')}`;
        app =
          a.status === ApplicationStatus.InProgress
            ? `${app}, ${(a.applicationCompletionRate * 100).toFixed(0).toString()}%)`
            : `${app})`;
        return app;
      });

      const ha = apps.filter(
        (a: any) => a.applicationCycleActive == 0 && a.programId == selectedProgramId
      );
      const historicAppFrequency: any = {};
      ha.forEach((a: any) => {
        if (historicAppFrequency[a.applicationType]) {
          if (historicAppFrequency[a.applicationType][a.statusDescription]) {
            historicAppFrequency[a.applicationType][a.statusDescription]++;
          } else {
            historicAppFrequency[a.applicationType][a.statusDescription] = 1;
          }
        } else {
          historicAppFrequency[a.applicationType] = {};
          historicAppFrequency[a.applicationType][a.statusDescription] = 1;
        }
      });

      const historicPartnerApps: any = [];
      Object.keys(historicAppFrequency).forEach((a) => {
        Object.keys(historicAppFrequency[a]).forEach((s) => {
          historicPartnerApps.push(
            `${a
              .replace('Tree City', 'TC')
              .replace('Tree Cities', 'TC')
              .replace('-Specific', '')}, ${s
              .replace('Pending ', '')
              .replace(
                'Arbor Day Foundation',
                userTypeAsString(UserType.Adf)
              )} (${historicAppFrequency[a][s]})`
          );
        });
      });

      const main = apps.find(
        (a: any) => a.applicationTypeId == 1 && a.programId == selectedProgramId
      );
      if (main) {
        if (partnerType == 'modal') {
          setPartnerContactLoading(true);
        } else {
          setSelectedPartnerContactLoading(true);
        }

        getOneApplication(main.applicationId).then((app) => {
          if (app) {
            getPartnerContact(app.applicationContactId).then((result: any) => {
              if (result) {
                const contact = getPartnerPrimaryContact(result);
                if (partnerType == 'modal') {
                  setModalPartnerAppPrimaryContact(contact);
                  setPartnerContactLoading(false);
                } else {
                  setSelectedPartnerAppPrimaryContact(contact);
                  setSelectedPartnerContactLoading(false);
                }
              } else {
                setPartnerContactLoading(false);
                setSelectedPartnerContactLoading(false);
              }
            });
          } else {
            setPartnerContactLoading(false);
            setSelectedPartnerContactLoading(false);
          }
        });
      }

      return { current: currentPartnerApps, historic: historicPartnerApps };
    },
    [getPartnerContact, getOneApplication, getPartnerPrimaryContact, selectedProgramId]
  );

  useEffect(() => {
    if (partnerInfo) {
      setModalPartnerId(null);
      setModalPartnerApps([]);
      setModalPartnerHistoricApps([]);
      setModalPartnerContact([]);
      setModalPartnerAppPrimaryContact([]);

      setSelectedPartner({});
      setSelectedPartnerId(null);
      setSelectedPartnerApps([]);
      setSelectedPartnerHistoricApps([]);
      setSelectedPartnerContact([]);
      setSelectedPartnerAppPrimaryContact([]);

      setPartnerOptions([]);

      setModalPartner(partnerInfo);

      if (partnerInfo.id) {
        setModalPartnerId(partnerInfo.id);
        setPartnerAppsLoading(true);
        getPartnerApps(partnerInfo.id).then((result: any) => {
          const apps = getPartnerCurrentApps(result, 'modal');
          setModalPartnerApps(apps['current']);
          setModalPartnerHistoricApps(apps['historic']);
          setPartnerAppsLoading(false);
        });
      }

      getPartnerContact(partnerInfo.primaryContactId).then((result: any) => {
        if (result) {
          const contact = getPartnerPrimaryContact(result);
          setModalPartnerContact(contact);
        }
      });
    }
  }, [
    partnerInfo,
    getPartnerApps,
    getPartnerContact,
    getPartnerCurrentApps,
    getPartnerPrimaryContact,
  ]);

  useEffect(() => {
    if (partnerInfo) {
      const po: any = [];
      selectedPartners.forEach((partner: any) => {
        if (
          (partnerInfo.countryId == USCountryId && partner.stateId == partnerInfo.stateId) ||
          (selectedProgramId == ProgramNames.TreeCitiesWorld &&
            partner.countryId == partnerInfo.countryId)
        ) {
          const label = `${partner.partnerName} (${partner.id})`;
          po.push({
            id: partner.id,
            label: label,
          });
        }
      });
      po.sort((a: any, b: any) => {
        if (a.label < b.label) {
          return -1;
        } else if (a.label > b.label) {
          return 1;
        }

        return 0;
      });

      setPartnerOptions(po);

      if (partnerInfo.id) {
        setModalPartnerId(partnerInfo.id);
      }
    }
  }, [partnerInfo, selectedPartners, selectedProgramId]);

  const getPartnerYearsRecognized = (partner: PartnerProps) => {
    const yearsRecognized = [];

    if (partner && partner.partnerYearsRecognized) {
      if (selectedProgramId == ProgramNames.TreeCityUSA) {
        yearsRecognized.push(
          `Tree City USA: ${YearsRecognizedByProgram(
            partner.partnerYearsRecognized,
            ProgramNames.TreeCityUSA,
            ApplicationTypes.Standard
          )} yrs`
        );
        yearsRecognized.push(
          `Growth Award: ${YearsRecognizedByProgram(
            partner.partnerYearsRecognized,
            ProgramNames.TreeCityUSA,
            ApplicationTypes.GrowthAward
          )} yrs`
        );
      } else if (selectedProgramId == ProgramNames.TreeCitiesWorld) {
        yearsRecognized.push(
          `Tree City of the World: ${YearsRecognizedByProgram(
            partner.partnerYearsRecognized,
            ProgramNames.TreeCitiesWorld
          )} yrs`
        );
      } else if (selectedProgramId == ProgramNames.TreeLineUSA) {
        yearsRecognized.push(
          `Tree Line: ${YearsRecognizedByProgram(
            partner.partnerYearsRecognized,
            ProgramNames.TreeLineUSA
          )} yrs`
        );
      } else if (selectedProgramId == ProgramNames.TreeCampusHigherEducation) {
        yearsRecognized.push(
          `Tree Campus Higher Ed: ${YearsRecognizedByProgram(
            partner.partnerYearsRecognized,
            ProgramNames.TreeCampusHigherEducation
          )} yrs`
        );
      } else if (selectedProgramId == ProgramNames.TreeCampusHealthcare) {
        yearsRecognized.push(
          `Tree Campus Healthcare: ${YearsRecognizedByProgram(
            partner.partnerYearsRecognized,
            ProgramNames.TreeCampusHealthcare
          )} yrs`
        );
      } else if (selectedProgramId == ProgramNames.TreeCampusK12) {
        yearsRecognized.push(
          `Tree Campus K-12: ${YearsRecognizedByProgram(
            partner.partnerYearsRecognized,
            ProgramNames.TreeCampusK12
          )} yrs`
        );
      }
    }
    return yearsRecognized;
  };

  useEffect(() => {
    if (partnerOptions.length > 0) {
      const o = partnerOptions.find((s: any) => s.id == modalPartnerId);
      setSelectedModalPartnerOption(o);
    }
  }, [modalPartnerId, partnerOptions]);

  useEffect(() => {
    if (selectedPartnerId && partnerOptions.length > 0) {
      const o = partnerOptions.find((s) => s.id == selectedPartnerId);
      setSelectedPartnerOption(o);
    }
  }, [partnerOptions, selectedPartnerId]);

  const handleClose = () => {
    setShowWarning(false);
    setPartnerMergeModalOpen(false);
  };

  return (
    <>
      <Dialog open={partnerMergeModalOpen} onClose={handleClose} maxWidth='md'>
        <DialogTitleWithClose onClose={handleClose}>Merge Partners</DialogTitleWithClose>
        <DialogContent>
          <Grid container columns={2} rowSpacing={2} columnSpacing={4}>
            <Grid size={1}>
              <Typography variant='h3'>Partner to retain</Typography>
            </Grid>
            <Grid size={1}>
              <Typography variant='h3'>Partner to merge</Typography>
            </Grid>

            <Grid size={1}>
              <FormControl fullWidth>
                <Autocomplete
                  id='partner-select-retain'
                  value={selectedModalPartnerOption}
                  getOptionLabel={(option) => {
                    return option.label;
                  }}
                  renderOption={(props, option) => {
                    return (
                      <li {...props} key={option.id}>
                        {option.label}
                      </li>
                    );
                  }}
                  onChange={(e, val) => {
                    if (val) {
                      const id = val.id;
                      const p = selectedPartners.find((partner: any) => partner.id == id);
                      if (!p?.id) return;

                      setModalPartner(p);
                      setModalPartnerId(Number(id));

                      getPartnerContact(p.primaryContactId).then((result: any) => {
                        if (result) {
                          const contact = getPartnerPrimaryContact(result);
                          setModalPartnerContact(contact);
                        }
                      });

                      setPartnerAppsLoading(true);

                      getPartnerApps(p.id).then((result: any) => {
                        const apps = getPartnerCurrentApps(result, 'modal');
                        setModalPartnerApps(apps['current']);
                        setModalPartnerHistoricApps(apps['historic']);
                        setPartnerAppsLoading(false);
                      });
                    }
                  }}
                  options={partnerOptions}
                  isOptionEqualToValue={(option, value) => {
                    if (!value) return false;
                    return option.id === value.id;
                  }}
                  renderInput={(params) => <TextField {...params} label={'Partner'} />}
                ></Autocomplete>
              </FormControl>
            </Grid>
            <Grid size={1}>
              <FormControl fullWidth>
                <Autocomplete
                  id='partner-select-merge'
                  value={selectedPartnerOption}
                  getOptionLabel={(option) => {
                    return option.label;
                  }}
                  renderOption={(props, option) => {
                    return (
                      <li {...props} key={option.id}>
                        {option.label}
                      </li>
                    );
                  }}
                  onChange={(e, val) => {
                    if (!val) return;
                    const id = val.id;

                    getPartnerDataGridDetails(id, selectedProgramId).then(
                      (partnerDetails: PartnerDashboardDetailsProps) => {
                        setSelectedPartner(partnerDetails);
                        setSelectedPartnerId(Number(id));

                        getPartnerContact(partnerDetails.primaryContactId).then((result: any) => {
                          if (result) {
                            const contact = getPartnerPrimaryContact(result);
                            setSelectedPartnerContact(contact);
                          }
                        });

                        setSelectedPartnerAppsLoading(true);
                        if (partnerDetails.id) {
                          getPartnerApps(partnerDetails.id).then((result: any) => {
                            if (result) {
                              const apps = getPartnerCurrentApps(result, 'selected');
                              setSelectedPartnerApps(apps['current']);
                              setSelectedPartnerHistoricApps(apps['historic']);
                            }
                            setSelectedPartnerAppsLoading(false);
                          });
                        }
                      }
                    );
                  }}
                  options={partnerOptions}
                  isOptionEqualToValue={(option, value) => {
                    if (!value) return false;
                    return option.id === value.id;
                  }}
                  renderInput={(params) => <TextField {...params} label={'Partner'} />}
                ></Autocomplete>
              </FormControl>
            </Grid>

            {partnerInfo && (
              <>
                <Grid size={1}>
                  <Box>
                    <Typography variant='overline2'>Shipping Address</Typography>
                    <Typography color='textSecondary'>
                      <div>{partnerInfo?.shippingContact?.streetAddress1}</div>
                      <div>{partnerInfo?.shippingContact?.streetAddress2}</div>
                      <div>{`${partnerInfo?.shippingContact?.city}, ${getStateName(
                        partnerInfo?.stateId
                      )} ${partnerInfo?.shippingContact?.zipCode}`}</div>
                      <div>{getCountryName(partnerInfo?.countryId)}</div>
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={1}>
                  <Box>
                    <Typography variant='overline2'>Shipping Address</Typography>
                    <Typography color='textSecondary'>
                      <div>{selectedPartner?.shippingContact?.streetAddress1}</div>
                      <div>{selectedPartner?.shippingContact?.streetAddress2}</div>
                      <div>
                        {selectedPartner?.shippingContact?.city
                          ? `${selectedPartner?.shippingContact?.city}, ${getStateName(
                              selectedPartner?.shippingContact?.stateId
                            )} ${selectedPartner?.shippingContact?.zipCode}`
                          : ''}
                      </div>
                      <div>
                        {selectedPartner?.countryId
                          ? getCountryName(selectedPartner?.countryId)
                          : ''}
                      </div>
                    </Typography>
                  </Box>
                </Grid>
              </>
            )}

            <Grid size={1}>
              <Box>
                <Typography variant='overline2'>Primary Contact</Typography>
                <Typography color='textSecondary'>
                  {modalPartnerContact.map((a: any) => {
                    return <div key={a}>{a}</div>;
                  })}
                </Typography>
              </Box>
            </Grid>
            <Grid size={1}>
              <Box>
                <Typography variant='overline2'>Primary Contact</Typography>
                <Typography color='textSecondary'>
                  {selectedPartnerContact.map((a: any) => {
                    return <div key={a}>{a}</div>;
                  })}
                </Typography>
              </Box>
            </Grid>

            <Grid size={1}>
              <Box>
                <Typography variant='overline2'>Years Awarded</Typography>
                <Typography color='textSecondary'>
                  {getPartnerYearsRecognized(modalPartner).map((a: any) => {
                    return <div key={a}>{a}</div>;
                  })}
                </Typography>
              </Box>
            </Grid>
            <Grid size={1}>
              <Box>
                <Typography variant='overline2'>Years Awarded</Typography>
                <Typography color='textSecondary'>
                  {getPartnerYearsRecognized(selectedPartner).map((a: any) => {
                    return <div key={a}>{a}</div>;
                  })}
                </Typography>
              </Box>
            </Grid>

            <Grid size={1}>
              <Box>
                <Typography variant='overline2'>Current Applications</Typography>
                <Typography color='textSecondary'>
                  {partnerAppsLoading ? (
                    <CircularProgress
                      size={'20px'}
                      style={{
                        color: 'rgb(0, 0, 0, 0.54)',
                        pointerEvents: 'none',
                      }}
                    />
                  ) : (
                    modalPartnerApps.map((a: any) => {
                      return <div key={a}>{a}</div>;
                    })
                  )}
                </Typography>
              </Box>
            </Grid>
            <Grid size={1}>
              <Box>
                <Typography variant='overline2'>Current Applications</Typography>
                <Typography color='textSecondary'>
                  {selectedPartnerAppsLoading ? (
                    <CircularProgress
                      size={'20px'}
                      style={{
                        color: 'rgb(0, 0, 0, 0.54)',
                        pointerEvents: 'none',
                      }}
                    />
                  ) : (
                    selectedPartnerApps.map((a: any) => {
                      return <div key={a}>{a}</div>;
                    })
                  )}
                </Typography>
              </Box>
            </Grid>

            <Grid size={1}>
              <Box>
                <Typography variant='overline2'>Current Application Primary Contact</Typography>
                <Typography color='textSecondary'>
                  {partnerContactLoading ? (
                    <CircularProgress
                      size={'20px'}
                      style={{
                        color: 'rgb(0, 0, 0, 0.54)',
                        pointerEvents: 'none',
                      }}
                    />
                  ) : (
                    modalPartnerAppPrimaryContact.map((a: any) => {
                      return <div key={a}>{a}</div>;
                    })
                  )}
                </Typography>
              </Box>
            </Grid>
            <Grid size={1}>
              <Box>
                <Typography variant='overline2'>Current Application Primary Contact</Typography>
                <Typography color='textSecondary'>
                  {selectedPartnerContactLoading ? (
                    <CircularProgress
                      size={'20px'}
                      style={{
                        color: 'rgb(0, 0, 0, 0.54)',
                        pointerEvents: 'none',
                      }}
                    />
                  ) : (
                    selectedPartnerAppPrimaryContact.map((a: any) => {
                      return <div key={a}>{a}</div>;
                    })
                  )}
                </Typography>
              </Box>
            </Grid>

            <Grid size={1}>
              <Box>
                <Typography variant='overline2'>Historic Applications (Count)</Typography>
                <Typography color='textSecondary'>
                  {partnerAppsLoading ? (
                    <CircularProgress
                      size={'20px'}
                      style={{
                        color: 'rgb(0, 0, 0, 0.54)',
                        pointerEvents: 'none',
                      }}
                    />
                  ) : (
                    modalPartnerHistoricApps.map((a: any) => {
                      return <div key={a}>{a}</div>;
                    })
                  )}
                </Typography>
              </Box>
            </Grid>
            <Grid size={1}>
              <Box>
                <Typography variant='overline2'>Historic Applications (Count)</Typography>
                <Typography color='textSecondary'>
                  {selectedPartnerAppsLoading ? (
                    <CircularProgress
                      size={'20px'}
                      style={{
                        color: 'rgb(0, 0, 0, 0.54)',
                        pointerEvents: 'none',
                      }}
                    />
                  ) : (
                    selectedPartnerHistoricApps.map((a: any) => {
                      return <div key={a}>{a}</div>;
                    })
                  )}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {showWarning && (
            <Alert severity='warning' style={{ marginTop: '20px' }}>
              <div>{`Sure you want to merge these partners?`}</div>
              <div>
                This will delete the merging partner and associated data. You cannot undue this
                action.
              </div>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {!showWarning && (
            <>
              <Button variant='secondary' size='large' onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant='primary'
                size='large'
                onClick={() => {
                  setShowWarning(true);
                }}
                disabled={postingPartner || modalPartnerId == null || selectedPartnerId == null}
              >
                {postingPartner ? (
                  <>
                    <span>Merging ... </span>
                    <CircularProgress
                      style={{
                        color: theme.palette.grey[500],
                        marginLeft: '5px',
                      }}
                      size='16px'
                    />
                  </>
                ) : (
                  'Merge Partners'
                )}
              </Button>
            </>
          )}
          {showWarning && (
            <>
              <Button variant='secondary' size='large' onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant='primary'
                size='large'
                disabled={postingPartner}
                onClick={() => {
                  setShowWarning(false);
                  updatePartnerMerge();
                }}
              >
                Continue
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
